import { cn } from "@/lib/utils";
import { extractImageUrl } from "@/utils/generic";
import { PanelRight } from "lucide-react";
import Image from "next/image";
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { NoxusIcon } from "./UI/NoxusIcon";

type AppPreviewProps = {
  logo?: string;
  icon?: string;
  isForm?: boolean;
  isSmall?: boolean;
  customization?: Record<string, any>;
};

export function AppPreview({
  isForm,
  isSmall,
  logo,
  icon,
  customization,
}: AppPreviewProps) {
  const appearTime = 0.75;
  const timeBetween = 0.5;
  const LOOP_MS = 20000;

  const [cycleKey, setCycleKey] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setCycleKey((k) => k + 1), LOOP_MS);
    return () => clearInterval(id);
  }, []);

  const fadeUp = (delay: number) => ({
    initial: { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: appearTime, delay },
  });

  const agentLogo = customization?.apply_custom_theme
    ? extractImageUrl(customization?.logo)
    : null;
  const sidebarLogo = customization?.apply_custom_theme
    ? extractImageUrl(customization?.sidebar_logo)
    : null;

  const { baseColor, shadeColor } = useMemo(() => {
    if (!customization?.apply_custom_theme || !customization?.main_color) {
      return {
        baseColor: "var(--sidebar)",
        shadeColor: "var(--sidebar-accent)",
      };
    }

    const { primary, accent } = customization.main_color as {
      primary: string;
      accent: string;
      secondary: string;
    };

    // Default theme or CSS variable defaults — no color overrides
    if (
      (primary === "#1E1E1E" && accent === "#F0F5FA") ||
      primary.startsWith("var(") ||
      accent.startsWith("var(")
    ) {
      return {
        baseColor: "var(--sidebar)",
        shadeColor: "var(--sidebar-accent)",
      };
    }

    // Convert primary to 15% opacity for sidebar items, matching the deployed app
    let translucent = primary;
    const m = primary.match(
      /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/
    );
    if (m) {
      translucent = `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, 0.15)`;
    }

    return {
      baseColor: accent,
      shadeColor: translucent,
    };
  }, [customization]);

  return (
    <div
      data-testid="app-preview"
      data-preview={isForm ? "form" : "chat"}
      className={cn("flex w-full rounded-md p-4 gap-4 border border-border")}
      style={{ backgroundColor: baseColor }}
    >
      {/* Sidebar */}
      <div className="flex flex-col flex-2">
        <div className="flex items-center justify-between gap-4">
          {sidebarLogo ? (
            <img
              src={sidebarLogo}
              alt="Sidebar logo"
              className="h-4 max-w-16 object-contain"
            />
          ) : logo ? (
            <Image src={logo} alt="Logo" width={24} height={24} />
          ) : (
            <div className="h-full transition-all duration-250 ease-in-out max-w-16">
              <Image
                src="https://storage.googleapis.com/image-storage-spot-manual/app/sidenav/noxus.svg"
                alt={"Noxus text logo"}
                style={{
                  height: "100%",
                  width: "auto",
                }}
                width={75}
                height={16}
                className="dark:invert dark:brightness-0"
              />
            </div>
          )}
          {!isSmall && (
            <PanelRight
              className="size-4 shrink-0"
              strokeWidth={1.25}
              color="var(--foreground)"
            />
          )}
        </div>
        <div className="my-2 mb-4 h-px w-full bg-border" />
        <div className="space-y-2">
          <div
            className="h-6 rounded-md"
            style={{ backgroundColor: shadeColor }}
          />
          <div
            className="h-6 rounded-md"
            style={{ backgroundColor: shadeColor }}
          />
        </div>
        <div className="flex-1" />
      </div>

      {/* Main content */}
      <div
        className="flex flex-col flex-3 rounded-md bg-card p-4 w-full gap-6"
        key={cycleKey}
      >
        {/* Top bubble area */}

        {isForm ? (
          <motion.div
            {...(isSmall ? {} : fadeUp(timeBetween))}
            className="flex flex-col w-3/4 self-center justify-center align-center gap-2 min-h-60"
          >
            <div className="relative flex justify-start w-full mb-4">
              <div className="h-6 w-6 rounded-md border border-border flex items-center justify-center">
                <NoxusIcon icon={icon} size="md" />
              </div>
            </div>
            <div className="h-6 w-full rounded-md bg-border" />
            <div className="h-12 w-full rounded-md bg-border" />
            <div
              className="h-6 w-full rounded-md mt-4"
              style={{ backgroundColor: baseColor }}
            />
          </motion.div>
        ) : (
          <>
            <motion.div
              {...(isSmall ? {} : fadeUp(0))}
              className="flex flex-col rounded-lg px-3 py-2 w-5/7 self-end"
              style={{ backgroundColor: baseColor }}
            >
              <div
                className="h-3 rounded-full"
                style={{ backgroundColor: shadeColor }}
              />
              <div
                className="mt-1 h-3 w-[80%] rounded-full"
                style={{ backgroundColor: shadeColor }}
              />
            </motion.div>

            {/* Message 1 */}
            <motion.div
              {...fadeUp(timeBetween)}
              className="flex items-start gap-4"
            >
              {!isSmall && (
                <div className="relative">
                  <div className="h-6 w-6 rounded-md border border-border flex items-center justify-center overflow-hidden">
                    {agentLogo ? (
                      <img
                        src={agentLogo}
                        alt="Agent logo"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <NoxusIcon icon={icon} size="sm" />
                    )}
                  </div>
                </div>
              )}
              <div className="flex-1 space-y-1">
                <div className="h-3 w-3/4 rounded-full bg-border" />
                <div className="h-3 w-3/4 rounded-full bg-border" />
                <div className="h-3 w-1/2 rounded-full bg-border" />
              </div>
            </motion.div>

            {/* Message 2 bubble */}
            {!isSmall && (
              <>
                <motion.div
                  {...fadeUp(timeBetween * 2)}
                  className="flex flex-col rounded-lg px-3 py-2 ml-26"
                  style={{ backgroundColor: baseColor }}
                >
                  <div
                    className="h-3 rounded-full"
                    style={{ backgroundColor: shadeColor }}
                  />
                </motion.div>

                {/* Message 3 */}
                <motion.div
                  {...fadeUp(timeBetween * 3)}
                  className="flex items-start gap-4 mb-15"
                >
                  <div className="relative">
                    <div className="h-6 w-6 rounded-md border border-border flex items-center justify-center overflow-hidden">
                      {agentLogo ? (
                        <img
                          src={agentLogo}
                          alt="Agent logo"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <NoxusIcon icon={icon} size="sm" />
                      )}
                    </div>
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="h-3 w-3/4 rounded-full bg-border" />
                    <div className="h-3 w-1/3 rounded-full bg-border" />
                  </div>
                </motion.div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AppPreview;
